import colors from 'colors'
import Button from 'components/Button'
import { Corner2 } from 'components/Corner'
import { eqNumber } from 'fp-ts/lib/Eq'
import images from 'images'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { animated } from 'react-spring'
import styled from 'styled-components'
import { Branch, Tree } from 'types'
import { O, pipe } from 'utils/fp'
import { useFadeIn } from 'utils/hooks'

const Container = styled(animated.div)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`
const Prompt = styled.div`
  color: ${colors.darkBeige};
`
const Content = styled.div`
  width: 100%;
  margin-top: 16px;
  padding: 24px 16px;
  box-sizing: border-box;

  background-color: white;
  border-radius: 8px;
`
const Question = styled.div`
  display: flex;
  align-items: flex-start;
`
const QuestionIdx = styled.div`
  padding: 0 8px 4px;
  margin-right: 8px;

  color: ${colors.beige};
  font-size: 32px;
  font-weight: 500;
  border-right: 1px dashed ${colors.beige};
`
const QuestionBody = styled.div`
  font-size: 16px;
  line-height: 24px;
`
const AnswerList = styled.div`
  margin-top: 8px;
`
const Answer = styled.div<{ selected: boolean }>`
  padding: 20px;
  margin: 16px 0;
  box-sizing: border-box;

  user-select: none;
  cursor: pointer;
  font-size: 14px;
  border-radius: 8px;
  color: ${props => (props.selected ? 'white' : colors.black)};
  border: ${props =>
    `1px solid ${props.selected ? colors.beige : colors.gray}`};
  background-color: ${props => (props.selected ? colors.beige : 'white')};
  box-shadow: ${props =>
    `0px 4px 0px 0px ${props.selected ? colors.beige : colors.beige}`};

  transition: color 0.3s, background-color 0.3s, border 0.3s, box-shadow 0.3s;
`
const Bottom = styled.div`
  width: 100%;
  margin-top: 24px;
  display: flex;
  justify-content: space-between;
`
const PrevBtn = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  border-radius: 24px;
  border: 1px solid ${colors.gray};
  box-shadow: 0px 4px 0px 0px ${colors.darkBeige};
  cursor: pointer;
`
const PrevBtnIcon = styled.img.attrs({ src: images.back })`
  width: 24px;
  height: 24px;
`
const NextBtn = styled(Button)<{ enabled: boolean }>`
  width: 96px;
  box-sizing: border-box;

  opacity: ${props => (props.enabled ? 1 : 0.6)};
  color: ${props => (props.enabled ? 'white' : colors.gray)};
  background-color: ${props => (props.enabled ? colors.beige : 'white')};
  border: ${props => (props.enabled ? 'none' : `1px solid ${colors.gray}`)};
`

interface Props {
  tree: Branch
  idx: number
  onPressPrev: () => void
  onPressNext: (nextTree: Tree) => void
}

const idxFormat = (idx: number) => {
  const num = idx + 1
  if (num < 10) return `0${num}`
  return `${num}`
}

const Step: FC<Props> = ({ tree, idx, onPressPrev, onPressNext }) => {
  const [selectedIdx, setSelectedIdx] = useState<O.Option<number>>(O.none)
  const nextTree = useMemo(
    () =>
      pipe(
        selectedIdx,
        O.chain(a => O.fromNullable(tree.children[a])),
        O.map(a => a.tree),
      ),
    [tree, selectedIdx],
  )
  useEffect(() => {
    setSelectedIdx(O.none)
  }, [tree])
  const anim = useFadeIn()

  return (
    <Container style={anim}>
      <Corner2 />
      <Prompt>仮想通貨投資タイプ診断</Prompt>
      <Content>
        <Question>
          <QuestionIdx>{idxFormat(idx)}</QuestionIdx>
          <QuestionBody>{tree.title}</QuestionBody>
        </Question>
        <AnswerList>
          {tree.children.map((a, idx) => (
            <Answer
              key={idx.toString()}
              selected={O.getEq(eqNumber).equals(selectedIdx, O.some(idx))}
              onClick={() => setSelectedIdx(O.some(idx))}
            >
              {a.edge}
            </Answer>
          ))}
        </AnswerList>
      </Content>
      <Bottom>
        <PrevBtn onClick={onPressPrev}>
          <PrevBtnIcon />
        </PrevBtn>
        <NextBtn
          enabled={O.isSome(nextTree)}
          onClick={() => {
            pipe(nextTree, O.map(onPressNext))
          }}
        >
          次へ
        </NextBtn>
      </Bottom>
    </Container>
  )
}

export default Step
